import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppModeProvider, useAppMode } from './context/AppModeContext';
import Navbar from './components/Navbar';
import LoginPage from './pages/LoginPage';
import OAuthCallbackPage from './pages/OAuthCallbackPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SetPasswordPage from './pages/SetPasswordPage';
import UpdatePasswordPage from './pages/UpdatePasswordPage';
import AddOrganisationPage from './pages/AddOrganisationPage';
import AddUserPage from './pages/AddUserPage';
import DashboardPage from './pages/DashboardPage';
import ReportsListPage from './pages/ReportsListPage';
import CreateReportPage from './pages/CreateReportPage';
import ReportEditorPage from './pages/ReportEditorPage';
import LocationsPage from './pages/LocationsPage';
import OrganisationsPage from './pages/OrganisationsPage';
import OrganisationSettingsPage from './pages/OrganisationSettingsPage';
import OrganisationUsersPage from './pages/OrganisationUsersPage';
import TrainingRecordsPage from './pages/TrainingRecordsPage';
import OrgDocumentsPage from './pages/OrgDocumentsPage';
import LicenceAdminPage from './pages/LicenceAdminPage';
import OrgLicencesPage from './pages/OrgLicencesPage';
import ProfilePermissionsPage from './pages/ProfilePermissionsPage';
import ModeChooserPage from './pages/ModeChooserPage';
import DataEntryPage from './pages/DataEntryPage';
import './App.css';

function PrivateRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function PublicRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Navigate to="/" /> : children;
}

function AdminRoute({ children }) {
  const { isAuthenticated, isAnyAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return isAnyAdmin ? children : <Navigate to="/" />;
}

function SystemAdminRoute({ children }) {
  const { isAuthenticated, isSystemAdmin } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" />;
  return isSystemAdmin ? children : <Navigate to="/" />;
}

function SiteFooter() {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) return null;
  return (
    <footer className="site-footer">
      &copy; 2026 Pocket Data Ltd. All rights reserved.
    </footer>
  );
}

function AppRoutes() {
  const { isAuthenticated } = useAuth();
  const { needsModeSelection, isDataEntryMode } = useAppMode();

  if (!isAuthenticated) {
    return (
      <div className="app-layout">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </div>
    );
  }

  if (needsModeSelection) {
    return <ModeChooserPage />;
  }

  if (isDataEntryMode) {
    return (
      <Routes>
        <Route path="/" element={<DataEntryPage />} />
        <Route path="/reports/new" element={<CreateReportPage />} />
        <Route path="/reports/:id" element={<ReportEditorPage />} />
        <Route path="/reports/:id/view" element={<ReportEditorPage readOnly />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  return (
    <div className="app-layout">
      <Navbar />
      <main className="app-main">
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
          <Route path="/reset-password" element={<PublicRoute><ResetPasswordPage /></PublicRoute>} />
          <Route path="/set-password" element={<PublicRoute><SetPasswordPage /></PublicRoute>} />
          <Route path="/" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/update-password" element={<PrivateRoute><UpdatePasswordPage /></PrivateRoute>} />
          <Route path="/organisations" element={<SystemAdminRoute><OrganisationsPage /></SystemAdminRoute>} />
          <Route path="/add-organisation" element={<SystemAdminRoute><AddOrganisationPage /></SystemAdminRoute>} />
          <Route path="/organisation-settings" element={<AdminRoute><OrganisationSettingsPage /></AdminRoute>} />
          <Route path="/add-user" element={<PrivateRoute><AddUserPage /></PrivateRoute>} />
          <Route path="/sites" element={<PrivateRoute><LocationsPage /></PrivateRoute>} />
          <Route path="/organisation-users" element={<AdminRoute><OrganisationUsersPage /></AdminRoute>} />
          <Route path="/reports" element={<PrivateRoute><ReportsListPage /></PrivateRoute>} />
          <Route path="/reports/new" element={<PrivateRoute><CreateReportPage /></PrivateRoute>} />
          <Route path="/reports/:id" element={<PrivateRoute><ReportEditorPage /></PrivateRoute>} />
          <Route path="/reports/:id/view" element={<PrivateRoute><ReportEditorPage readOnly /></PrivateRoute>} />
          <Route path="/training-records" element={<PrivateRoute><TrainingRecordsPage /></PrivateRoute>} />
          <Route path="/org-documents" element={<PrivateRoute><OrgDocumentsPage /></PrivateRoute>} />
          <Route path="/licences" element={<SystemAdminRoute><LicenceAdminPage /></SystemAdminRoute>} />
          <Route path="/org-licences" element={<AdminRoute><OrgLicencesPage /></AdminRoute>} />
          <Route path="/profile-permissions" element={<AdminRoute><ProfilePermissionsPage /></AdminRoute>} />
        </Routes>
      </main>
      <SiteFooter />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppModeProvider>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </AppModeProvider>
    </BrowserRouter>
  );
}

export default App;
